import { useApolloClient } from "@apollo/client";
import { getDataOrNull, omit } from "@msys/common";
import {
  CardItem,
  DataGrid,
  DebouncedSearchInput,
  ErrorMessage,
  GridColDef,
  ListHeader,
  ListHeaderActiveFilters,
  ListHeaderActiveFiltersActionButton,
  LoadingSpinner,
  ModalOpenButton,
  useScreenWidth,
} from "@msys/ui";
import { Stack, TablePagination, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Link, useNavigate } from "react-router-dom";
import { OrdersSorting, OrderStatus } from "../../../clients/graphqlTypes.js";
import { SwitchCollectionViewButton } from "../../commons/button/SwitchCollectionViewButton.js";
import { useFiltersAndPagination } from "../../commons/filters/useFiltersAndPagination.js";
import {
  CollectionView,
  useCollectionViewWithMobile,
} from "../../commons/hooks/useCollectionView.js";
import { Page, PageTopbarItem } from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import {
  PAGE_LIST_RESULTS_PER_PAGE,
  RESULTS_PER_PAGE_OPTIONS,
} from "../../constants.js";
import {
  PurchaseOrderStatusFilterButton,
  PurchaseOrderStatusFilterChips,
} from "../../features/documents/filters/PurchaseOrderStatus.js";
import { NodePurchaseOrderListItem } from "../../features/purchase-orders/PurchaseOrderListItem.js";
import { PurchaseOrderStatusBadge } from "../../features/purchase-orders/PurchaseOrderStatusBadge.js";
import { PurchaseOrderTitle } from "../../features/purchase-orders/PurchaseOrderTitle.js";
import { useDataGridStateStore } from "../../features/users/useDataGridStateStore.js";
import {
  DocumentsPurchaseOrders_OrderNodeFragment,
  DocumentsPurchaseOrdersQueryVariables,
  useDocumentsPurchaseOrdersQuery,
} from "./PurchaseOrders.generated.js";
import { useMemo } from "react";
import { PurchaseOrdersFilterModal } from "../../features/documents/PurchaseOrdersFilterModal.js";
import { FilterButton } from "../../commons/filters/FilterButton.js";

const ALLOWED_VIEWS: CollectionView[] = ["table", "list"];

export type Filters = Omit<
  DocumentsPurchaseOrdersQueryVariables,
  "limit" | "offset" | "orderingCapability" | "filterStatus"
> & {
  filterStatus?: OrderStatus[];
};

const DEFAULT_SORTING: OrdersSorting[] = [
  {
    column: "createdAt",
    direction: "desc",
  },
];

interface Props {
  submenuItems: PageTopbarItem[];
}

export function PurchaseOrders({ submenuItems }: Props) {
  const navigate = useNavigate();
  const { t } = useTranslate([
    "Global",
    "ProjectOverview",
    "PurchaseOrders",
    "SidebarItems",
  ]);
  const { isMinTablet } = useScreenWidth();

  const [activeView, setActiveView] =
    useCollectionViewWithMobile<CollectionView>("orders", "table", "list");

  const {
    offset,
    limit,
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
    resetFilters,
  } = useFiltersAndPagination<OrdersSorting, Filters>(
    {},
    DEFAULT_SORTING,
    PAGE_LIST_RESULTS_PER_PAGE
  );

  const serializedFilters = useMemo<
    Partial<DocumentsPurchaseOrdersQueryVariables>
  >(() => {
    const { filterStatus, ...restFilters } = filters;
    const serialized: Partial<DocumentsPurchaseOrdersQueryVariables> = {
      ...restFilters,
    };
    if (filterStatus && filterStatus.length === 1) {
      serialized.filterStatus = filterStatus[0];
    }
    return serialized;
  }, [filters]);

  const QUERY_BASE_VARIABLES = {
    limit,
    offset,
    sorting: DEFAULT_SORTING,
  };

  const client = useApolloClient();
  const query = useDocumentsPurchaseOrdersQuery({
    client,
    variables: {
      ...QUERY_BASE_VARIABLES,
      ...serializedFilters,
    },
  });
  const orders =
    getDataOrNull((query.data ?? query.previousData)?.orders)?.edges.map(
      e => e.node
    ) ?? [];
  const totalCount =
    getDataOrNull((query.data ?? query.previousData)?.orders)?.totalCount ?? 0;

  const stateStore = useDataGridStateStore("PurchaseOrders");
  const activeFilterChips = [];
  if (filters.filterStatus) {
    activeFilterChips.push(
      <PurchaseOrderStatusFilterChips
        key="status"
        values={filters.filterStatus}
        setValues={
          setFilters
            ? value =>
                value && value.length > 0
                  ? setFilters(filters => ({
                      ...filters,
                      filterStatus: value,
                    }))
                  : setFilters(filters => omit(filters, "filterStatus"))
            : undefined
        }
      />
    );
  }

  return (
    <Page
      subtitle={t("Documents", {
        ns: "SidebarItems",
      })}
      title={t("Purchase Orders", {
        ns: "PurchaseOrders",
      })}
      submenuItems={submenuItems}
    >
      <PageContainer>
        <ListHeader
          SwitchViewButton={
            <SwitchCollectionViewButton
              allowedViews={ALLOWED_VIEWS}
              activeView={activeView}
              setActiveView={setActiveView}
            />
          }
          QuickFilter={
            <Stack direction="row" spacing={1} alignItems="center">
              {isMinTablet ? (
                <PurchaseOrderStatusFilterButton
                  value={filters.filterStatus ?? []}
                  setValue={newValue =>
                    setFilters(state =>
                      newValue.length > 0
                        ? {
                            ...state,
                            filterStatus: newValue,
                          }
                        : omit(state, "filterStatus")
                    )
                  }
                />
              ) : undefined}
            </Stack>
          }
          FilterButton={
            !isMinTablet ? (
              <ModalOpenButton
                Modal={PurchaseOrdersFilterModal}
                modalProps={{
                  filters,
                  setFilters,
                  resetFilters,
                }}
              >
                <FilterButton />
              </ModalOpenButton>
            ) : undefined
          }
          ActiveFilterChips={
            <ListHeaderActiveFilters
              activeFilterChips={activeFilterChips}
              clearAllButton={
                <ListHeaderActiveFiltersActionButton
                  label={t("Clear all", { ns: "Global" })}
                  onClick={resetFilters}
                />
              }
              collapsedContent={
                <Typography variant="body2">
                  {t("Active filters", { ns: "Global" })}
                  <Typography
                    variant="body2"
                    component="span"
                    marginLeft={1}
                    fontWeight="bold"
                  >
                    {t("{count} results", {
                      ns: "Global",
                      count: activeFilterChips.length,
                    })}
                  </Typography>
                </Typography>
              }
            />
          }
          SearchField={
            <DebouncedSearchInput
              placeholder={t("Search", {
                ns: "Global",
              })}
              defaultValue={filters.searchTerm}
              onChangeSearchTerm={(newValue: string) => {
                setFilters(filters => ({
                  ...filters,
                  searchTerm: newValue,
                }));
              }}
            />
          }
          marginBottom={2}
        />

        {(() => {
          switch (activeView) {
            case "table":
              return (
                <DataGrid
                  stateStore={stateStore}
                  loading={query.loading}
                  hideFooter={totalCount === 0}
                  columns={
                    [
                      {
                        field: "order-number",
                        headerName: t("Order No", {
                          ns: "PurchaseOrders",
                        }),
                        flex: 1,
                        minWidth: 100,
                        sortable: false,
                        renderCell: ({ row: order }) => order.number,
                      },
                      {
                        field: "status",
                        headerName: t("Status", {
                          ns: "Global",
                        }),
                        flex: 1,
                        minWidth: 100,
                        sortable: false,
                        renderCell: ({ row: order }) => (
                          <PurchaseOrderStatusBadge order={order} />
                        ),
                      },
                      {
                        field: "title",
                        headerName: t("Title", {
                          ns: "Global",
                        }),
                        flex: 3,
                        minWidth: 100,
                        sortable: false,
                        renderCell: ({ row: order }) => (
                          <PurchaseOrderTitle order={order} />
                        ),
                      },
                      {
                        field: "projectNumber",
                        headerName: t("Project No", {
                          ns: "PurchaseOrders",
                        }),
                        flex: 1,
                        minWidth: 100,
                        sortable: false,
                        renderCell: ({ row: order }) => order.projectNumber,
                      },
                      {
                        field: "clientName",
                        headerName: t("Client name", {
                          ns: "PurchaseOrders",
                        }),
                        flex: 2,
                        minWidth: 100,
                        sortable: false,
                        renderCell: ({ row: order }) => order.clientName,
                      },
                      {
                        field: "supplierName",
                        headerName: t("Supplier name", {
                          ns: "PurchaseOrders",
                        }),
                        flex: 2,
                        minWidth: 100,
                        sortable: false,
                        renderCell: ({ row: order }) => order.supplierName,
                      },
                    ] as GridColDef<DocumentsPurchaseOrders_OrderNodeFragment>[]
                  }
                  rows={orders}
                  onRowClick={({ row: order }) =>
                    navigate(
                      `/projects/${order.projectId}/materials/purchase-orders/${order.id}`
                    )
                  }
                  paginationModel={paginationModel}
                  onPaginationModelChange={newPaginationModel => {
                    setPaginationModel(newPaginationModel);
                  }}
                  pageSizeOptions={RESULTS_PER_PAGE_OPTIONS}
                  rowCount={totalCount}
                />
              );
            case "list":
              if (query.loading) return <LoadingSpinner />;
              if (totalCount === 0)
                return (
                  <ErrorMessage
                    message={t("There are no items to display", {
                      ns: "Global",
                    })}
                  />
                );
              return (
                <Stack width="100%" direction="column" spacing={1}>
                  {orders.map(order => (
                    <CardItem
                      key={order.id}
                      //@ts-ignore
                      component={Link}
                      to={`/projects/${order.projectId}/materials/purchase-orders/${order.id}`}
                    >
                      <NodePurchaseOrderListItem order={order} />
                    </CardItem>
                  ))}
                  <TablePagination
                    component="div"
                    count={totalCount}
                    page={paginationModel.page}
                    onPageChange={(event, newPage) => {
                      setPaginationModel({
                        pageSize: paginationModel.pageSize,
                        page: newPage,
                      });
                    }}
                    rowsPerPage={paginationModel.pageSize}
                    onRowsPerPageChange={event => {
                      setPaginationModel({
                        pageSize: parseInt(event.target.value, 10),
                        page: 0,
                      });
                    }}
                    labelRowsPerPage={t("Per page:", { ns: "Global" })}
                  />
                </Stack>
              );
          }
        })()}
      </PageContainer>
    </Page>
  );
}
